import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Helmet from 'helmet/helmet';
import { Query } from '@apollo/client/react/components';

import ImageView from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import LoadingProgress from 'lib/react-router/loading-progress';

import { Site } from 'four-nets/site';
import ErrorPage from 'four-nets/errors/error-page';
import Header from 'four-nets/photoblog/posts/common/header/header';
import Controls from 'four-nets/photoblog/posts/common/feed-images/description/controls';
import ImageMore from 'four-nets/photoblog/posts/common/feed-images/image-more/image-more';
import iconLeft from 'four-nets/icons/svg/icon_arrow-left.svg';
import iconRight from 'four-nets/icons/svg/icon_arrow-right.svg';
import iconPhoto from 'four-nets/icons/svg/icon_photo.svg';
import { displayedName } from 'four-nets/user/utils';
import { RelatedInspiration } from 'four-nets/forum/related-content/related-inspirations';

import SelectionItems from '../posts/common/selection-items/selection-items';

import imageWidgetsQuery, { ImageWidgetsQuery, ImageWidgetsQueryVariables } from './image-widgets.graphql';
import { NotificationImageFragment } from './image.graphql';
import { PostType } from './image.graphql.types';
import defaultStyles from './image.scss';
import ImageTitle from '../posts/common/feed-images/description/image-title/image-title';

export interface Props {
    image: NotificationImageFragment;
    commentId?: string;
    styles: typeof defaultStyles;
}

interface State {
    moreOpen: boolean;
}

class Image extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    state = { moreOpen: false };

    openMore = () => {
        this.setState({
            moreOpen: true,
        });
    };

    closeMore = () => {
        this.setState({
            moreOpen: false,
        });
    };

    renderHelmet = (H: React.ComponentClass) => {
        const { image } = this.props;
        const {
            post: { user, title: postTitle },
            href,
            title,
            position,
        } = image;
        const { site } = this.context;

        return (
            <FormattedMessage
                id='ALBUM_OF_USER_%(username)s'
                values={{
                    gender: user && user.profile && user.profile.sex === 1 ? 'f' : 'm',
                    username: user && displayedName(user),
                }}
            >
                {userAlbum => (
                    <H>
                        <title>
                            {`${postTitle} - ${title ? `${title} -` : ''} ${userAlbum} - Foto ${position} - ${
                                site.name
                            }`}
                        </title>
                        ,
                        <meta property='og:title' content={title as string} />,
                        <meta property='og:url' content={href || site.name} />,
                    </H>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { image, styles, commentId } = this.props;
        const { moreOpen } = this.state;
        return (
            <Query<ImageWidgetsQuery, ImageWidgetsQueryVariables>
                query={imageWidgetsQuery}
                variables={{
                    imageId: image.id,
                }}
            >
                {({ loading, error, data }) => (
                    <LoadingProgress loading={loading}>
                        <ErrorPage error={error}>
                            <div className={styles.wrapper}>
                                <div className={styles.imageWrapper}>
                                    <Helmet render={this.renderHelmet} />

                                    <div className={styles.headerWrapper}>
                                        <Header
                                            lastChangeTime={image.post.lastChangeTime}
                                            postType={image.post.postType}
                                            newImagesCount={0}
                                            user={image.post.user}
                                        />
                                        {image.post.postType === 'POST_SHORT_MESSAGE' ? (
                                            <div className={styles.shortMessageTitle}>{image.post.title}</div>
                                        ) : (
                                            <h1 className={styles.title}>
                                                <Link to={image.post.href} className={styles.titleLink}>
                                                    {image.post.title}
                                                </Link>
                                            </h1>
                                        )}
                                        <SelectionItems selectionItems={image.post.selectionItems} collapse={true} />
                                    </div>
                                    <div className={styles.image}>
                                        <ImageView
                                            linkify={false}
                                            srcSet={image.photoFile!.srcset!}
                                            src={image.photoFile!.src}
                                            sizes={{ xs: 320, md: 635 }}
                                            alt={image.alt}
                                            aspectFill={true}
                                        />
                                        <Link to={image.prevHref || ''} className={styles.prev}>
                                            <SVGIcon style={{ image: styles.icon }} src={iconLeft} />
                                        </Link>
                                        <Link to={image.nextHref || ''} className={styles.next}>
                                            <SVGIcon style={{ image: styles.icon }} src={iconRight} />
                                        </Link>
                                        <span className={styles.position}>
                                            <SVGIcon style={{ image: styles.photoIcon }} src={iconPhoto} />

                                            {`${image.position}/${image.post.imagesCount}`}
                                        </span>
                                    </div>
                                    <div>
                                        <ImageTitle
                                            style={{ imageTitle: styles.imageTitle }}
                                            id={image.id}
                                            title={image.title}
                                            postUserUsername={image.post.user?.username}
                                        />
                                        <Controls
                                            commentId={commentId}
                                            onMore={this.openMore}
                                            likesCount={image.likesCount || 0}
                                            inFeed={true}
                                            {...image}
                                        />
                                    </div>
                                    {image ? (
                                        <ImageMore
                                            node={image as any}
                                            open={moreOpen || false}
                                            onClose={this.closeMore}
                                        />
                                    ) : null}
                                </div>
                                <div className={styles.side}>
                                    {data?.image?.postImages?.edges.length ? (
                                        <div className={styles.postImagesWrapper}>
                                            {image.post.postType === 'POST_SHORT_MESSAGE' ? (
                                                <FormattedMessage id='SHORT_MESSAGE_POST_IMAGES'>
                                                    {msg => (
                                                        <h2 className={styles.postImagesTitle}>
                                                            {msg}{' '}
                                                            <Link
                                                                to={image.post.href as string}
                                                                className={styles.postImagesTitleLink}
                                                            >
                                                                <FormattedMessage id='FROM_SHORT_MESSAGE' />
                                                            </Link>
                                                        </h2>
                                                    )}
                                                </FormattedMessage>
                                            ) : (
                                                <FormattedMessage id='IMAGE_POST_IMAGES'>
                                                    {msg => (
                                                        <h2 className={styles.postImagesTitle}>
                                                            {msg}{' '}
                                                            <Link
                                                                to={image.post.href as string}
                                                                className={styles.postImagesTitleLink}
                                                            >
                                                                {image.post.title}
                                                            </Link>
                                                        </h2>
                                                    )}
                                                </FormattedMessage>
                                            )}
                                            <div className={styles.postImages}>
                                                {data.image.postImages.edges.map(edge => (
                                                    <Link
                                                        key={edge?.node?.href as string}
                                                        to={edge?.node?.href as string}
                                                        className={styles.postImage}
                                                    >
                                                        <ImageView
                                                            sizes={{ xs: 130 }}
                                                            src={edge?.node?.photoFile?.src!}
                                                            srcSet={edge?.node?.photoFile?.srcset}
                                                            aspectFill={true}
                                                            aspectRatio={1}
                                                            linkify={false}
                                                        />
                                                    </Link>
                                                ))}
                                            </div>
                                            <Link to={image?.post.href as string} className={styles.postImagesButton}>
                                                <FormattedMessage id='MORE_PHOTOS' />
                                            </Link>
                                        </div>
                                    ) : null}
                                    {data?.imageTagSlugs?.edges.length ? (
                                        <div className={styles.tagSlugs}>
                                            <FormattedMessage id='IMAGE_TAG_SLUGS'>
                                                {msg => <h2 className={styles.sideHeader}>{msg}</h2>}
                                            </FormattedMessage>
                                            <div className={styles.relatedInspirations}>
                                                {data.imageTagSlugs.edges.map(edge => (
                                                    <RelatedInspiration
                                                        style={{ inspiration: styles.inspiration }}
                                                        key={edge?.node?.id}
                                                        inspiration={edge?.node!}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    {data?.collections?.edges.length ? (
                                        <div className={styles.collectionsWrapper}>
                                            <FormattedMessage id='BELONGS_TO_COLLECTION'>
                                                {msg => <h2 className={styles.sideHeader}>{msg}</h2>}
                                            </FormattedMessage>
                                            {data.collections.edges.map(edge => (
                                                <span key={edge?.node?.id} className={styles.collectionItem}>
                                                    <Link
                                                        to={edge?.node?.href as string}
                                                        className={styles.collectionLink}
                                                    >
                                                        {edge?.node?.title}
                                                    </Link>
                                                    <span className={styles.collectionCount}>
                                                        ({edge?.node?.imagesCount})
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            {data?.image?.relatedImages?.edges.length ? (
                                <>
                                    <h2 className={styles.masonryTitle}>
                                        <FormattedMessage id='IMAGE_SIMILAR_POSTS' />
                                    </h2>
                                    <div className={styles.masonry}>
                                        {data.image.relatedImages.edges.map(edge => (
                                            <div key={edge?.node?.href as string} className={styles.masonryItem}>
                                                <Link to={edge?.node?.href as string} className={styles.masonryImage}>
                                                    <ImageView
                                                        sizes={{ xs: 206 }}
                                                        src={edge?.node?.photoFile?.src!}
                                                        srcSet={edge?.node?.photoFile?.srcset}
                                                        aspectFill={true}
                                                        linkify={false}
                                                    />
                                                </Link>
                                                <Link
                                                    to={edge?.node?.post.href as string}
                                                    className={styles.masonryLink}
                                                >
                                                    {edge?.node?.post.title}
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </ErrorPage>
                    </LoadingProgress>
                )}
            </Query>
        );
    }
}

export default Styled(defaultStyles)(Image);
